import {
  apiClient,
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  clearAuthTokens,
  getRefreshToken,
  setAuthTokens,
} from '@/services/apiClient';
import type { ApiEnvelope, CurrentUser, LoginResponse } from '@/types/api';

export interface LoginPayload {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const authService = {
  login: async (payload: LoginPayload) => {
    const result = await apiPost<LoginResponse>('/auth/login', payload);
    setAuthTokens(result, payload.rememberMe ?? false);
    return result;
  },

  logout: async () => {
    try {
      return await apiPost<null>('/auth/logout', { refreshToken: getRefreshToken() ?? undefined });
    } finally {
      clearAuthTokens();
    }
  },

  me: () => apiGet<CurrentUser>('/auth/me'),

  changePassword: async (payload: ChangePasswordPayload) => {
    const result = await apiPost<null>('/auth/change-password', payload);
    clearAuthTokens();
    return result;
  },

  forgotPassword: (email: string) => apiPost<{ devToken?: string }>('/auth/forgot-password', { email }),

  resetPassword: (payload: { token: string; newPassword: string; confirmPassword: string }) =>
    apiPost<null>('/auth/reset-password', payload),

  updateProfile: (payload: { name?: string; position?: string | null }) =>
    apiPatch<CurrentUser>('/private/users/me', payload),

  uploadAvatar: async (file: File): Promise<CurrentUser> => {
    const form = new FormData();
    form.append('file', file);

    const response = await apiClient.post<ApiEnvelope<CurrentUser>>('/private/users/me/avatar', form, {
      headers: { 'Content-Type': undefined },
    });
    return response.data.data;
  },

  removeAvatar: () => apiDelete<CurrentUser>('/private/users/me/avatar'),
};
